import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Simple module-level cache so we only hit the status endpoint once per session
let cachedStatus = null;
let cachedPromise = null;
let cachedToken = null;

const fetchAmazonStatus = async (token) => {
  const response = await fetch(`${API_URL}/auth/amazon/status`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Amazon status: ${response.status}`);
  }

  return response.json();
};

export const useAmazonStatus = (token) => {
  const [data, setData] = useState(cachedStatus);
  const [loading, setLoading] = useState(!cachedStatus);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      cachedToken = null;
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    // If the logged-in user/session changed, invalidate the module cache
    if (cachedToken !== token) {
      cachedToken = token;
      cachedStatus = null;
      cachedPromise = null;
    }

    // If we already have cached data, use it and skip refetch
    if (cachedStatus) {
      setData(cachedStatus);
      setLoading(false);
      setError(null);
      return;
    }

    // If a fetch is already in progress, reuse its promise
    if (!cachedPromise) {
      cachedPromise = fetchAmazonStatus(token)
        .then((result) => {
          cachedStatus = result;
          return result;
        })
        .finally(() => {
          cachedPromise = null;
        });
    }

    setLoading(true);
    setError(null);

    cachedPromise
      .then((result) => {
        setData(result);
      })
      .catch((err) => {
        setError(err);
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token]);

  const connected = !!(data && data.connected === true);

  return {
    data,
    connected,
    loading,
    error,
  };
};


